// Order the grocery list from one grocery app (Zepto, Swiggy Instamart…): choose the delivery
// address, match each ingredient to a product, choose packs and quantities, review the app's own
// preview (prices, fees, payment methods), and place the order only after an explicit confirm.
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { api, ApiError } from '../api';
import { Link } from '../router';
import { ComingSoon } from '../components/ComingSoon';
import { Back, Check } from '../icons';
import { ErrorBox, useToast } from '../components/Shell';
import { OrderResult } from '../components/OrderResult';
import { Thumb } from '../components/Thumb';
import { AddressCard, CartSummary, ReviewActions } from '../components/ReviewParts';
import { money, startConnect, useProviders } from '../commerce';
import { groceryIDs } from '../grocery-selection';
import { useT } from '../i18n';
import { useScript } from '../script';
import { load, save } from '../storage';
import type { Address, FoodOrder as Order, GroceryCart, GroceryMatch, MatchItem, Script } from '../types';

type Step = 'address' | 'pick' | 'review' | 'done';
interface Pick { on: boolean; product: number; qty: number }

function itemName(it: MatchItem, script: Script): [string, string] {
  const main = (script === 'ta' ? it.name_ta : script === 'tg' ? it.name_translit : it.name_en) || it.name_en;
  const sub = script === 'ta' ? it.name_en : it.name_ta;
  return [main, sub && sub !== main ? sub : ''];
}

export function GroceryOrder({ provider }: { provider: string }) {
  const t = useT();
  const [script] = useScript();
  const [providers] = useProviders();
  const p = providers.find((x) => x.id === provider);
  const name = p?.name ?? provider;
  const addrKey = `er.grocery.address.${provider}`;
  const ids = groceryIDs();
  const connectError = new URLSearchParams(location.search).get('connect_error');

  const [step, setStep] = useState<Step>('address');
  const [addresses, setAddresses] = useState<Address[] | null>(null);
  const [address, setAddress] = useState<Address | null>(null);
  const [match, setMatch] = useState<GroceryMatch | null>(null);
  const [picks, setPicks] = useState<Pick[]>([]);
  const [cart, setCart] = useState<GroceryCart | null>(null);
  const [method, setMethod] = useState('');
  const [order, setOrder] = useState<Order | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [toast, showToast] = useToast();
  const placing = useRef(false);

  useEffect(() => {
    if (!p?.connected) return;
    api.addresses(provider).then((as) => {
      setAddresses(as);
      const saved = as.find((a) => a.id === load<string>(addrKey, ''));
      if (saved) chooseAddress(saved);
    }).catch((e: ApiError) => setErr(e.message));
  }, [p?.connected]);

  const chooseAddress = (a: Address) => {
    setAddress(a);
    save(addrKey, a.id);
    setStep('pick');
    setMatch(null);
    setErr('');
    api.groceryMatch(provider, a.id, ids).then((m) => {
      setMatch(m);
      setPicks(m.items.map((it) => ({ on: it.candidates.length > 0 && !it.pantry, product: 0, qty: 1 })));
    }).catch((e: ApiError) => setErr(e.message));
  };

  const chosen = useMemo(() => (match?.items ?? []).flatMap((it, i) => {
    const pk = picks[i];
    return pk?.on && it.candidates[pk.product] ? [{ item_id: it.candidates[pk.product].id, quantity: pk.qty }] : [];
  }), [match, picks]);

  // Product details by id, for images and pack sizes on the review screen.
  const productByID = useMemo(() => new Map((match?.items ?? []).flatMap((it) => it.candidates.map((c) => [c.id, c] as const))), [match]);

  // Items only, at the provider's listed prices; fees and offers come with its preview.
  const itemsTotal = useMemo(() => (match?.items ?? []).reduce((sum, it, i) => {
    const pk = picks[i];
    const c = pk?.on ? it.candidates[pk.product] : undefined;
    return c ? sum + c.price.amount * pk.qty : sum;
  }, 0), [match, picks]);

  const review = async () => {
    if (!address) return;
    setBusy(true);
    setErr('');
    try {
      const c = await api.groceryCart(provider, address.id, chosen);
      setCart(c);
      setMethod(c.payments.length === 1 ? c.payments[0].method : '');
      setStep('review');
      window.scrollTo(0, 0);
    } catch (e) { setErr((e as ApiError).message); } finally { setBusy(false); }
  };

  const place = async () => {
    if (!address || !method || placing.current) return; // never double-submit an order
    placing.current = true;
    setBusy(true);
    setErr('');
    try {
      setOrder(await api.groceryOrder(provider, { address_id: address.id, payment_method: method, confirmed: true }));
      setStep('done');
      window.scrollTo(0, 0);
    } catch (e) {
      setErr((e as ApiError).message);
      placing.current = false;
    } finally { setBusy(false); }
  };

  const set = (i: number, patch: Partial<Pick>) => setPicks(picks.map((pk, k) => (k === i ? { ...pk, ...patch } : pk)));
  const methodLabel = cart?.payments.find((x) => x.method === method)?.label ?? '';
  const back = () => (step === 'review' ? setStep('pick') : step === 'pick' && addresses && addresses.length > 1 ? setStep('address') : history.back());

  if (p?.coming_soon) return <ComingSoon name={name} back="/grocery/order" />;
  return (
    <div class="page" style="padding-bottom:calc(120px + env(safe-area-inset-bottom))">
      <div class="backbar"><button class="icon-btn" aria-label={t('common.back')} onClick={back}><Back /></button></div>
      <div class="pad" style="display:flex;flex-direction:column;gap:4px">
        <h1 class="h1" style="font-size:28px">{step === 'review' ? t('food.reviewTitle') : step === 'done' ? t('food.doneTitle') : t('order.groceryTitle', { app: name })}</h1>
        {p?.attribution && <span class="small muted">{p.attribution}</span>}
      </div>

      {connectError && <div class="hint-box" style="margin:14px 16px 0" role="alert">{t('order.connectFailed', { app: name })}</div>}
      {ids.length === 0 && step !== 'done' && <div class="hint-box" style="margin:16px">{t('order.emptyList')} <Link href="/grocery">{t('grocery.title')}</Link></div>}

      {ids.length > 0 && p && !p.connected && (
        <section class="card" style="margin:16px;padding:18px;display:flex;flex-direction:column;gap:12px">
          <div class="display" style="font-weight:700;font-size:19px">{t('order.connectTitle', { app: name })}</div>
          <div class="muted" style="line-height:1.6">{t('order.connectBody2', { app: name })}</div>
          <button class="btn btn-primary" disabled={busy} onClick={() => { setBusy(true); startConnect(provider, `/grocery/order/${provider}`).catch((e: ApiError) => { setBusy(false); showToast(e.message); }); }}>
            {t('order.connect', { app: name })}
          </button>
        </section>
      )}

      {err && <ErrorBox message={err} onRetry={step === 'pick' && address ? () => chooseAddress(address) : undefined} />}

      {p?.connected && ids.length > 0 && step === 'address' && (
        <section style="padding:16px;display:flex;flex-direction:column;gap:10px">
          <h2 class="section-label" style="margin:0 4px">{t('food.deliverTo')}</h2>
          {addresses === null && !err && <div class="skeleton" style="height:120px" aria-label={t('common.loading')} />}
          {addresses?.length === 0 && <div class="hint-box">{t('food.noAddress', { app: name })}</div>}
          {addresses && addresses.length > 0 && (
            <div class="rows">
              {addresses.map((a) => (
                <button class="row" onClick={() => chooseAddress(a)}>
                  <span class="grow"><span style="font-weight:700">{a.label || t('food.address')}</span><span class="sub">{a.line}</span></span>
                </button>
              ))}
            </div>
          )}
        </section>
      )}

      {step === 'pick' && address && (
        <>
          <button class="row" style="margin:12px 16px 0;width:calc(100% - 32px);border:1px solid var(--line);border-radius:14px" onClick={() => setStep('address')}>
            <span class="grow"><span class="sub">{t('food.deliverTo')}</span><span style="font-weight:600">{address.label ? `${address.label} · ` : ''}{address.line}</span></span>
            <span class="small" style="color:var(--accent-text);font-weight:700">{t('food.change')}</span>
          </button>
          {!match && !err && (
            <div class="pad list" style="padding-top:16px">
              <div class="muted" role="status">{t('order.finding', { app: name })}</div>
              {[1, 2, 3].map(() => <div class="skeleton" style="height:96px" />)}
            </div>
          )}
          {match && (
            <>
              <div style="padding:14px 20px 4px;display:flex;align-items:baseline;justify-content:space-between;gap:8px">
                <span class="section-label">{t('order.pickedOf', { n: chosen.length, total: match.items.length })}</span>
                <span class="small muted">{t('order.basicsLeftOut')}</span>
              </div>
              <div class="pad list" style="padding-top:8px">
                {match.items.map((it, i) => {
                  const pk = picks[i];
                  const [main, sub] = itemName(it, script);
                  const c = it.candidates[pk?.product ?? 0];
                  const detail = [sub, it.needed && t('order.recipeNeeds', { q: it.needed }), it.pantry && t('order.pantry')].filter(Boolean).join(' · ');
                  return (
                    <article class={'pick-card' + (pk?.on ? '' : ' off')}>
                      <div style="display:flex;align-items:flex-start;gap:10px">
                        <button class="tick" aria-pressed={!!pk?.on} disabled={!it.candidates.length} aria-label={t('order.include', { item: main })} onClick={() => set(i, { on: !pk.on })}>
                          {pk?.on && <Check size={14} style="color:var(--ground)" />}
                        </button>
                        <span style="flex:1;min-width:0;display:flex;flex-direction:column;line-height:1.35">
                          <span class="ta-name" style="font-weight:700;font-size:17px" lang={script === 'ta' ? 'ta' : undefined}>{main}</span>
                          {detail && <span class="small muted">{detail}</span>}
                        </span>
                      </div>
                      {it.candidates.length === 0 && <span class="small muted">{t('order.notFound', { app: name })}</span>}
                      {c && pk?.on && (
                        <>
                          <div style="display:flex;gap:12px;align-items:center">
                            <Thumb src={c.image_url} name={c.title} size="lg" />
                            <span style="flex:1;min-width:0;display:flex;flex-direction:column;gap:2px">
                              <span class="clamp2" style="font-weight:700;font-size:15px;line-height:1.3">{c.title}</span>
                              {c.pack && <span class="small muted">{c.pack}</span>}
                              <span style="font-weight:800;font-size:17px;margin-top:2px">{money(c.price)}</span>
                            </span>
                            <span class="stepper">
                              <button aria-label={t('order.less')} disabled={pk.qty <= 1} onClick={() => set(i, { qty: pk.qty - 1 })}>−</button>
                              <span aria-live="polite">{pk.qty}</span>
                              <button aria-label={t('order.more')} disabled={pk.qty >= 20} onClick={() => set(i, { qty: pk.qty + 1 })}>+</button>
                            </span>
                          </div>
                          {it.candidates.length > 1 && (
                            <div style="display:flex;flex-direction:column;gap:6px">
                              <span class="small" style="font-weight:700;color:var(--muted)">{t('order.otherPacks')}</span>
                              <div class="alt-row" role="radiogroup" aria-label={t('order.pack')}>
                                {it.candidates.map((alt, k) => k === pk.product ? null : (
                                  <button class="alt" role="radio" aria-checked={false} aria-pressed={false} onClick={() => set(i, { product: k })}>
                                    <Thumb src={alt.image_url} name={alt.title} size="sm" />
                                    <span class="clamp2" style="font-size:12px;line-height:1.3;font-weight:600">{alt.pack || alt.title}</span>
                                    <span style="font-size:13px;font-weight:800">{money(alt.price)}</span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </article>
                  );
                })}
              </div>
              <div class="bottom-bar">
                <div class="bottom-bar-inner">
                  <span style="flex:1;display:flex;flex-direction:column;line-height:1.25">
                    <span style="font-weight:800;font-size:18px">{money({ amount: itemsTotal, currency: 'INR' })}</span>
                    <span class="small muted">{t('order.itemsFeesNext', { n: chosen.length })}</span>
                  </span>
                  <button class="btn btn-primary" style="min-height:54px;border-radius:16px;padding:0 22px" disabled={busy || chosen.length === 0} onClick={review}>
                    {busy ? t('order.building') : t('food.review')}
                  </button>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {step === 'review' && cart && address && (
        <section style="padding:16px 16px calc(190px + env(safe-area-inset-bottom));display:flex;flex-direction:column;gap:12px">
          <CartSummary totals={cart.totals} eta={cart.eta} lines={cart.lines.map((l) => {
            const c = productByID.get(l.item_id);
            return { key: l.item_id, title: l.title || c?.title || '', detail: c?.pack, qty: l.quantity, price: l.price, image: c?.image_url };
          })} />
          <AddressCard label={address.label} line={address.line} />
          {!cart.deliverable && <div class="hint-box" role="alert">{t('order.notDeliverable', { app: name })}</div>}
          <div style="display:flex;flex-direction:column;gap:8px">
            <h2 class="section-label" style="margin:0 4px">{t('food.payWith')}</h2>
            {cart.payments.length === 0 && <div class="hint-box">{t('food.noPayment', { app: name })}</div>}
            <div class="rows" role="radiogroup" aria-label={t('food.payWith')}>
              {cart.payments.map((pm) => (
                <button class="row" role="radio" aria-checked={method === pm.method} onClick={() => setMethod(pm.method)} style={method === pm.method ? 'background:var(--soft)' : ''}>
                  <span class="checkbox" style={`border-radius:999px;${method === pm.method ? 'background:var(--ink)' : ''}`} />
                  <span class="grow" style="font-weight:600">{pm.label}</span>
                </button>
              ))}
            </div>
          </div>
          {cart.messages?.map((m) => <div class="hint-box">{m}</div>)}
          <div class="small muted" style="line-height:1.5">{t('order.realOrder', { app: name })}</div>
          <ReviewActions appURL={p?.app_cart_url} appName={name} place={
            <button class="btn btn-primary btn-lg" disabled={busy || !method || !cart.deliverable} onClick={place}>
              {busy ? t('food.placing') : method ? t('food.place', { total: money(cart.total), method: methodLabel }) : t('food.choosePayment')}
            </button>
          } />
        </section>
      )}

      {step === 'done' && order && address && (
        <OrderResult provider={provider} name={name} order={order} addressId={address.id} onToast={showToast}
          onRetry={() => { placing.current = false; setOrder(null); setStep('review'); }} />
      )}
      {toast}
    </div>
  );
}
